import React from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { Routes } from '@hum/icm-app/src/state';
import { Text, TextType } from '@hum/ui-library';
import { ConnectorOption, ConnectorStatus, PlatformType } from '@hum/types';
import { useApiQuery, getConnectors, getConnectorConfigs } from '@hum/api';

import { ConnectorTab, Tabs } from './components';
import accountingIcon from './assets/accounting.png';
import paymentProcessorIcon from './assets/payment-processor.png';
import bankIcon from './assets/bank.png';

const TAB_INDEXES = {
  'accounting-system': 0,
  'payment-processor': 1,
  bank: 2,
};

export const ConnectorsPage = () => {
  const location = useLocation();
  const history = useHistory();
  const currentPath = location.pathname.split('/').slice(-1)[0];
  const defaultTabIndex = TAB_INDEXES[currentPath] || 0;

  // If we're at the base connectors route, redirect to accounting-system
  React.useEffect(() => {
    if (location.pathname === Routes.COMPANY_ACTIVATION_CONNECTORS) {
      history.replace(
        Routes.COMPANY_ACTIVATION_CONNECTORS_ACCOUNTING + location.search
      );
    }
  }, [location.pathname, history, location.search]);

  const { data: connections, isLoading: connectionsLoading } = useApiQuery(
    getConnectors
  );
  const { data: options } = useApiQuery(getConnectorConfigs);
  if (connections && options) {
    connections.forEach((connection: ConnectorOption) => {
      if (!connection.config) {
        const option = options.find(
          (optionConnection) =>
            optionConnection.config.connectorType === connection.connectorType
        );
        if (option) {
          connection.config = option.config;
        }
      }
    });
  }
  const filteredConnections = connections?.filter(
    (connection) =>
      connection.status &&
      (connection.status === ConnectorStatus.Active ||
        connection.status === ConnectorStatus.Authenticating)
  );
  const accountingSystemConnections = filteredConnections?.filter(
    (connection) =>
      connection.config?.platformType === PlatformType.AccountingSystem
  );
  const paymentProcessorConnections = filteredConnections?.filter(
    (connection) =>
      connection.config?.platformType === PlatformType.PaymentProcessor
  );
  const bankConnections = filteredConnections?.filter(
    (connection) => connection.config?.platformType === PlatformType.Bank
  );

  const connectorPageTabs = [
    {
      label: 'Accounting system',
      children: (
        <ConnectorTab
          connections={accountingSystemConnections}
          isLoading={connectionsLoading}
          icon={accountingIcon}
          title="Gain investor's trust"
          description="Investors are more likely to fund companies that share their data in a standard format. Connect your software and let Hum do all the formating for you."
        />
      ),
      path: Routes.COMPANY_ACTIVATION_CONNECTORS_ACCOUNTING,
    },
    {
      label: 'Payment processor',
      children: (
        <ConnectorTab
          connections={paymentProcessorConnections}
          isLoading={connectionsLoading}
          icon={paymentProcessorIcon}
          title="Get better terms"
          description="Increase your likelihood of getting better terms by giving investors a perspective of your financial health. All while getting insights on customer trends and product market fit."
        />
      ),
      path: Routes.COMPANY_ACTIVATION_CONNECTORS_PAYMENT,
    },
    {
      label: 'Bank',
      children: (
        <ConnectorTab
          isBank
          connections={bankConnections}
          isLoading={connectionsLoading}
          icon={bankIcon}
          title="Connect your bank accounts to access unique funding opportunities"
          description="Hum securely connects to your bank with Plaid, making it easy to link all relevant bank accounts. By connecting to your bank, you will increase the number of funding opportunities your business will be eligible for. You can link as many accounts as needed."
        />
      ),
      path: Routes.COMPANY_ACTIVATION_CONNECTORS_BANK,
    },
  ];

  return (
    <div data-testid="company-activation:connectors">
      <Text type={TextType.DISPLAY_S}>Connectors</Text>
      <Tabs tabs={connectorPageTabs} defaultTabIndex={defaultTabIndex} />
    </div>
  );
};
